import React from 'react';
import { CTAButton } from './CTAButton';
import { motion } from 'framer-motion';
import { Check, Heart, Zap, ShieldCheck } from 'lucide-react';
export function Hero() {
  const scrollToSystem = () => {
    const element = document.getElementById('system');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative pt-12 pb-0 md:pt-16 px-6 md:px-12 bg-cream overflow-hidden" data-id="element-175">
      {/* Subtle ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-terracotta/3 rounded-full blur-[120px] pointer-events-none" data-id="element-176" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-sage/3 rounded-full blur-[120px] pointer-events-none" data-id="element-177" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-178">
        {/* Badge + Headline */}
        <motion.div initial={{
        opacity: 0,
        y: -16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="flex flex-col items-center text-center mb-8" data-id="element-179">
          <div className="inline-block px-3.5 py-1.5 mb-7 rounded-full bg-terracotta/10 text-terracotta text-xs font-bold tracking-[0.15em] uppercase" data-id="element-180">
            For Newborn Parents · 0–3 Months
          </div>

          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium text-brown leading-[1.05] mb-0" data-id="element-181">
            You're Not Lazy.{' '}
            <span className="italic text-terracotta block" data-id="element-182">
              You're Nap-Trapped.
            </span>
          </h1>
        </motion.div>

        {/* Hero Image — immediately after headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: [0.25, 0.1, 0.25, 1],
        delay: 0.15
      }} className="w-full mb-6" data-id="element-183">
          <div className="relative w-full" data-id="element-184">
            {/* Ambient glow behind image */}
            <div className="absolute inset-0 -m-8 md:-m-12 bg-terracotta/5 rounded-full blur-3xl pointer-events-none" data-id="element-185" />
            <div className="absolute inset-0 -m-6 md:-m-8 bg-sage/4 rounded-full blur-2xl pointer-events-none" data-id="element-186" />

            {/* Image container with floating animation */}
            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative w-full rounded-2xl md:rounded-3xl overflow-hidden" style={{
            aspectRatio: '4/3',
            boxShadow: ['0 20px 60px rgba(196,133,106,0.18)', '0 8px 24px rgba(196,133,106,0.12)', '0 0 40px rgba(196,133,106,0.06)'].join(', ')
          }} data-id="element-187">
              <img src="https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png" alt="Mother holding her sleeping newborn in a calm, sunlit room" className="w-full h-full object-cover" style={{
              objectPosition: 'center 50%'
            }} data-id="element-188" />
            </motion.div>

            {/* Warm ground shadow that breathes with the float */}
            <motion.div animate={{
            scaleX: [1, 0.92, 1],
            opacity: [0.5, 0.35, 0.5]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-2/3 h-6 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, rgba(196,133,106,0.2) 0%, rgba(196,133,106,0.05) 70%, transparent 100%)',
            filter: 'blur(12px)'
          }} data-id="element-189" />
          </div>
        </motion.div>

        {/* Copy section — starts with subtitle */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
        delay: 0.3
      }} className="flex flex-col items-center text-center pb-14 md:pb-20" data-id="element-190">
          <p className="text-brown-light text-lg md:text-xl leading-relaxed mb-8 max-w-md" data-id="element-191">
            Get 30 protected minutes back.
            <br data-id="element-192" />
            In 7 days. Without cry-it-out.
          </p>

          {/* Emotional pre-CTA line */}
          <p className="font-serif text-lg md:text-xl italic text-brown/70 mb-7 leading-relaxed max-w-md" data-id="element-193">
            "You don't need perfect naps.
            <br data-id="element-194" />
            You just need 30 minutes back."
          </p>

          {/* Decorative accent line */}
          <div className="w-12 h-px bg-terracotta/30 mx-auto mb-8" data-id="element-195" />

          {/* Bullets */}
          <div className="flex flex-col gap-5 mb-10 items-start text-left" data-id="element-196">
            {[{
            main: 'A simple daily structure',
            sub: 'that protects your time'
          }, {
            main: 'Gentle crib steps —',
            sub: 'no tears, no guilt'
          }, {
            main: 'Designed for one hand,',
            sub: '3am, half asleep'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 text-brown-light" data-id="element-197">
                <div className="w-6 h-6 rounded-full bg-sage/15 flex items-center justify-center shrink-0 mt-0.5" data-id="element-198">
                  <Check className="w-3.5 h-3.5 text-sage-dark" strokeWidth={3} data-id="element-199" />
                </div>
                <div className="text-sm font-medium leading-relaxed" data-id="element-200">
                  {item.main}
                  <br data-id="element-201" />
                  <span className="text-brown-light/70" data-id="element-202">{item.sub}</span>
                </div>
              </div>)}
          </div>

          {/* CTA */}
          <CTAButton text="Yes — Give Me My 30 Minutes" className="w-full md:w-auto min-w-[300px] mb-4" data-id="element-203" />

          {/* Creator byline */}
          <p className="text-xs text-brown/40 mt-3 mb-6 font-medium tracking-wide" data-id="element-204">
            Created by a mother who lived this.
          </p>

          {/* Trust microcopy */}
          <div className="flex flex-wrap justify-center items-center gap-3 mt-1" data-id="element-205">
            {[{
            icon: Heart,
            label: 'No cry-it-out',
            color: 'text-sage-dark',
            bg: 'bg-sage/5',
            border: 'border-sage/10'
          }, {
            icon: Zap,
            label: 'Instant access',
            color: 'text-terracotta',
            bg: 'bg-terracotta/5',
            border: 'border-terracotta/10'
          }, {
            icon: ShieldCheck,
            label: '14-day guarantee',
            color: 'text-sage-dark',
            bg: 'bg-sage/5',
            border: 'border-sage/10'
          }].map((item, i) => {
            const Icon = item.icon;
            return <span key={i} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${item.bg} border ${item.border} text-xs font-semibold text-brown-light backdrop-blur-sm`} style={{
              boxShadow: '0 2px 8px rgba(61,50,41,0.03), 0 1px 2px rgba(61,50,41,0.04)'
            }} data-id="element-206">
                  <Icon className={`w-3.5 h-3.5 ${item.color}`} strokeWidth={2.5} data-id="element-207" />
                  {item.label}
                </span>;
          })}
          </div>

          <button onClick={scrollToSystem} className="mt-8 text-xs text-brown-light/50 hover:text-brown-light/80 transition-all duration-200 underline underline-offset-4 decoration-brown-light/20 hover:decoration-brown-light/40" data-id="element-208">
            See how it works ↓
          </button>
        </motion.div>
      </div>
    </section>;
}